'use client';

import { c as _c } from "react/compiler-runtime";
import { usePathname } from 'next/navigation';
import type { FC } from 'react';
import { useEffect } from 'react';
import { debounce } from 'throttle-debounce';
import { useAuth } from '@sola/auth-sdk/store.tsx';
import { useCart } from '@sola/cart-sdk/store.tsx';
// import { useAuth } from '@sola/auth-sdk/store.tsx';
import { generalConfig } from '@sola/config/lib/getters.ts';
import { funnelLabels } from '@sola/contentful-model/lib/taxonomy-values.ts';
import type { AuthClientResponse } from '@sola/schema/dotcom/auth.ts';
import { UserLoginState } from '@sola/schema/dotcom/auth.ts';
import type { CartItem } from '@sola/schema/dotcom/cart/cart-item.ts';
import { CartItemTypeEnum } from '@sola/schema/dotcom/cart/cart-item.ts';
import type { Cart } from '@sola/schema/dotcom/cart/cart.ts';
import { CommitmentFrequencyEnum } from '@sola/schema/dotcom/cart/commitments.ts';
import { SponsorshipItemTypeEnum } from '@sola/schema/dotcom/cart/sponsorship.ts';
import type { ChildAvailabilityItem } from '@sola/schema/dotcom/children/child.ts';
import type { AnyObject } from '@sola/schema/dotcom/helpers.ts';
import type { FundProps } from '@sola/schema/lib/schema.ts';
import { mapAnalyticsCartItems } from '@sola/shared-utils/analytics.ts';

/* eslint-disable @typescript-eslint/naming-convention */

declare global {
  var dataLayer: AnyObject[];
}
const trackEvent = (event: AnyObject) => {
  if (!globalThis) {
    return;
  }
  if (!globalThis.dataLayer) {
    globalThis.dataLayer = [];
  }
  globalThis.dataLayer.push(event);
};

// https://developers.google.com/analytics/devguides/collection/ga4/views?client_type=gtag
export const trackPageView = (experiments?: string[]) => {
  trackEvent({
    event: 'page_view',
    page_location: globalThis.location.href,
    page_title: document.title,
    send_page_view: true,
    experiments
  });
};
export const TrackPageView: FC<{
  experiments?: string[];
}> = t0 => {
  const $ = _c(4);
  const {
    experiments
  } = t0;
  const pathname = usePathname();
  let t1;
  if ($[0] !== experiments) {
    t1 = () => {
      trackPageView(experiments);
    };
    $[0] = experiments;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== pathname) {
    t2 = [pathname];
    $[2] = pathname;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  useEffect(t1, t2);
  return null;
};

// https://ciorg.atlassian.net/wiki/spaces/DX/pages/29026287630/Clicks
// @TODO: Add click tracking to lots of places.
export const trackClick = (label: string, link?: string, classname?: string, elementId?: string) => {
  trackEvent({
    event: 'element_click',
    // The inner text of the element clicked
    element_label: label,
    // The `href` attribute of the element clicked - sometimes null
    element_link: link || null,
    // The CSS class name of the element clicked - sometimes null
    element_class_name: classname || null,
    // The `id` HTML attribute of the element clicked - sometimes null
    element_id: elementId || null
  });
};

// https://support.google.com/analytics/answer/9216061 — GA4 file_download
export const trackFileDownload = (fileName: string, fileUrl: string) => {
  trackEvent({
    event: 'file_download',
    file_name: fileName,
    link_url: fileUrl
  });
};

// https://ciorg.atlassian.net/wiki/spaces/DX/pages/29025861652/Generate+Lead
export const trackLeadFormSuccess = (formIdentifier: string) => {
  trackEvent({
    event: 'generate_lead',
    // The name of the lead generation form
    lead_source: formIdentifier
  });
};

// https://ciorg.atlassian.net/wiki/spaces/DX/pages/29026582549/Search
export const trackSearch = (searchTerm?: string) => {
  // Don't track empty strings
  if (!searchTerm) {
    return;
  }
  trackEvent({
    event: 'search',
    // The term the user searched for
    search_term: searchTerm
  });
};

// https://ciorg.atlassian.net/wiki/spaces/DX/pages/29026582663/Funnel+Page+View
export const trackFunnelPage = (funnel?: string | null) => {
  if (!funnel || !funnelLabels[funnel as keyof typeof funnelLabels]) {
    return;
  }
  trackEvent({
    event: 'funnel_page_view',
    funnel_category: funnelLabels[funnel as keyof typeof funnelLabels]
  });
};
export const TrackFunnelPage: FC<{
  funnel?: string | null;
}> = t0 => {
  const $ = _c(3);
  const {
    funnel
  } = t0;
  let t1;
  let t2;
  if ($[0] !== funnel) {
    t1 = () => {
      trackFunnelPage(funnel);
    };
    t2 = [funnel];
    $[0] = funnel;
    $[1] = t1;
    $[2] = t2;
  } else {
    t1 = $[1];
    t2 = $[2];
  }
  useEffect(t1, t2);
  return null;
};

// https://ciorg.atlassian.net/wiki/spaces/DX/pages/29027893249/Select+Content
export const trackSelectContent = (cmsId: string, title: string, type: string, tags: string[]) => {
  trackEvent({
    event: 'select_content',
    // CMS ID of the content
    content_id: cmsId,
    // Title of the content
    content_title: title,
    // Category of the content,
    content_type: type,
    // Array of tags associated with the story
    content_tags: tags
  });
};

// https://ciorg.atlassian.net/wiki/spaces/DX/pages/29026091384/View+Content
export const trackViewContent = (cmsId: string, title: string, type: string, tags: string[]) => {
  trackEvent({
    event: 'view_content',
    // CMS ID of the content
    content_id: cmsId,
    // Title of the content
    content_title: title,
    // Category of the content,
    content_type: type,
    // Array of tags associated with the story
    content_tags: tags
  });
};
export const TrackViewContent: FC<{
  cmsId: string;
  title?: string;
  type?: string;
  tags?: string[];
}> = t0 => {
  const $ = _c(6);
  const {
    cmsId,
    title,
    type,
    tags
  } = t0;
  let t1;
  let t2;
  if ($[0] !== cmsId || $[1] !== tags || $[2] !== title || $[3] !== type) {
    t1 = () => {
      trackViewContent(cmsId, title || "Unknown", type || "Unknown", tags || []);
    };
    t2 = [cmsId, title, type, tags];
    $[0] = cmsId;
    $[1] = tags;
    $[2] = title;
    $[3] = type;
    $[4] = t1;
    $[5] = t2;
  } else {
    t1 = $[4];
    t2 = $[5];
  }
  useEffect(t1, t2);
  return null;
};

// https://ciorg.atlassian.net/wiki/spaces/DX/pages/29026385954/Search+for+a+Child
export type ChildSearchEvent = {
  gender?: string;
  ageMin?: number;
  ageMax?: number;
  region?: string;
  country?: string | string[];
  birthMonth?: number;
  birthDay?: number;
  orphan?: boolean;
  disability?: boolean;
  highlyVulnerable?: boolean;
  hiv?: boolean;
};
export const trackChildSearch = (event: ChildSearchEvent) => {
  trackEvent({
    event: 'search_for_a_child',
    filter_gender: event.gender,
    filter_age_min: event.ageMin,
    filter_age_max: event.ageMax,
    filter_region: event.region,
    filter_country: Array.isArray(event.country) ? event.country.join(', ') : event.country,
    filter_birth_month: event.birthMonth,
    filter_birth_day: event.birthDay,
    filter_orphan: event.orphan,
    filter_disabilities: event.disability,
    filter_hvc: event.highlyVulnerable,
    filter_hiv: event.hiv
  });
};
export const TrackChildSearch: FC<{
  event: ChildSearchEvent;
}> = t0 => {
  const $ = _c(5);
  const {
    event
  } = t0;
  let t1;
  let t2;
  if ($[0] !== event) {
    t1 = () => {
      debounce(5000, () => {
        trackChildSearch(event);
      })();
    };
    t2 = JSON.stringify(event);
    $[0] = event;
    $[1] = t1;
    $[2] = t2;
  } else {
    t1 = $[1];
    t2 = $[2];
  }
  let t3;
  if ($[3] !== t2) {
    t3 = [t2];
    $[3] = t2;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  useEffect(t1, t3);
  return null;
};
const getFundCodeFrequency = (fund: FundProps) => {
  const defaultResult = {
    commitmentFrequency: CommitmentFrequencyEnum.OneTime,
    fundCode: fund.fundCodeOneTime
  };
  if (fund.availableFrequencies?.includes(CommitmentFrequencyEnum.OneTime)) {
    return defaultResult;
  }
  if (!fund.availableFrequencies?.includes(CommitmentFrequencyEnum.OneTime)) {
    return {
      commitmentFrequency: CommitmentFrequencyEnum.Monthly,
      fundCode: fund.fundCodeCommitment || fund.fundCodeOneTime
    };
  }
  return defaultResult;
};
const convertFundToCart = (fund: FundProps, isCatalog?: boolean): Partial<CartItem> => {
  const {
    commitmentFrequency,
    fundCode
  } = getFundCodeFrequency(fund);
  return {
    itemType: CartItemTypeEnum.Fund,
    itemCode: fundCode,
    title: fund.fundTitle,
    // TODO: Ideally we'd be able to pull the default selection from a component
    commitmentFrequency: isCatalog ? CommitmentFrequencyEnum.OneTime : commitmentFrequency,
    unitPrice: isCatalog ? fund.catalogAmount ?? 0 : fund.suggestedAmount || fund.suggestedAmountsOneTime?.find(a => a.isDefault)?.value || fund.suggestedAmountsMonthly?.find(a => a.isDefault)?.value || fund.suggestedAmountsAnnually?.find(a => a.isDefault)?.value || 0,
    quantity: 1,
    fund: {
      fundCode: fundCode || ''
    }
  };
};
export const TrackViewFund: FC<{
  fund?: FundProps;
}> = t0 => {
  const $ = _c(4);
  const {
    fund
  } = t0;
  let t1;
  if ($[0] !== fund) {
    t1 = () => {
      if (!fund) {
        return;
      }
      trackEvent({
        event: "view_item",
        items: mapAnalyticsCartItems([convertFundToCart(fund)]),
        currency: "USD"
      });
    };
    $[0] = fund;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const t2 = fund?.id;
  let t3;
  if ($[2] !== t2) {
    t3 = [t2];
    $[2] = t2;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  useEffect(t1, t3);
  return null;
};
export const trackViewFundItemList = (listId: string, listName: string, funds: FundProps[], isCatalog: boolean) => {
  if (!funds?.length) {
    return;
  }
  trackEvent({
    event: 'view_item_list',
    item_list_id: listId,
    item_list_name: listName,
    items: mapAnalyticsCartItems(funds.map(fund => convertFundToCart(fund, isCatalog))),
    currency: 'USD'
  });
};
export const TrackViewFundItemList: FC<{
  listId: string;
  listName: string;
  items: FundProps[];
  isCatalog?: boolean;
}> = t0 => {
  const $ = _c(11);
  const {
    listId,
    listName,
    items,
    isCatalog: t1
  } = t0;
  const isCatalog = t1 === undefined ? false : t1;
  let t2;
  if ($[0] !== isCatalog || $[1] !== items || $[2] !== listId || $[3] !== listName) {
    t2 = () => {
      if (!items?.length) {
        return;
      }
      trackViewFundItemList(listId, listName, items, isCatalog);
    };
    $[0] = isCatalog;
    $[1] = items;
    $[2] = listId;
    $[3] = listName;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  let t3;
  if ($[5] !== items) {
    t3 = JSON.stringify(items);
    $[5] = items;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  let t4;
  if ($[7] !== listId || $[8] !== listName || $[9] !== t3) {
    t4 = [listId, listName, t3];
    $[7] = listId;
    $[8] = listName;
    $[9] = t3;
    $[10] = t4;
  } else {
    t4 = $[10];
  }
  useEffect(t2, t4);
  return null;
};

// https://ciorg.atlassian.net/wiki/spaces/DX/pages/29027696645/Select+Item
export const trackSelectFundItem = (listId: string, listName: string, fund?: FundProps, isCatalog = false) => {
  if (!fund) {
    return;
  }
  trackEvent({
    event: 'select_item',
    item_list_id: listId,
    item_list_name: listName,
    items: mapAnalyticsCartItems([convertFundToCart(fund, isCatalog)]),
    currency: 'USD'
  });
};
const convertChildToCart = (item: ChildAvailabilityItem): Partial<CartItem> => {
  return {
    // TODO: Ideally this would come from the SDG ContentSettings.fundCodeCdsp
    itemCode: 'DS',
    itemType: CartItemTypeEnum.Sponsorship,
    commitmentFrequency: CommitmentFrequencyEnum.Monthly,
    quantity: 1,
    sponsorship: {
      sponsorshipType: SponsorshipItemTypeEnum.Selected,
      globalId: item.globalId,
      sourceCode: item.sourceCode ?? undefined
    },
    child: {
      name: item.name?.preferred,
      globalId: item.globalId,
      localId: item.localId,
      imageUrl: '',
      birthDate: '',
      country: item.fieldOffice
    },
    // TODO: Currently a non-exported constant in cart's sponsorship.ts
    title: 'Child Sponsorship',
    // TODO: Ideally this would come from the SDG ContentSettings.defaultCdspRate or a subsidy...
    unitPrice: generalConfig.rate
  };
};
export const trackViewSacItemList = (listId: string, listName: string, items: ChildAvailabilityItem[]) => {
  if (!items?.length) {
    return;
  }
  trackEvent({
    event: 'view_item_list',
    item_list_id: listId,
    item_list_name: listName,
    items: mapAnalyticsCartItems(items.map(convertChildToCart)),
    currency: 'USD'
  });
};
export const TrackViewSacItemList: FC<{
  listId: string;
  listName: string;
  items: ChildAvailabilityItem[];
}> = t0 => {
  const $ = _c(10);
  const {
    listId,
    listName,
    items
  } = t0;
  let t1;
  if ($[0] !== items || $[1] !== listId || $[2] !== listName) {
    t1 = () => {
      if (!items?.length) {
        return;
      }
      trackViewSacItemList(listId, listName, items);
    };
    $[0] = items;
    $[1] = listId;
    $[2] = listName;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  let t2;
  if ($[4] !== items) {
    t2 = JSON.stringify(items);
    $[4] = items;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  let t3;
  if ($[6] !== listId || $[7] !== listName || $[8] !== t2) {
    t3 = [listId, listName, t2];
    $[6] = listId;
    $[7] = listName;
    $[8] = t2;
    $[9] = t3;
  } else {
    t3 = $[9];
  }
  useEffect(t1, t3);
  return null;
};
export const TrackViewBio: FC<{
  child: ChildAvailabilityItem;
}> = t0 => {
  const $ = _c(4);
  const {
    child
  } = t0;
  let t1;
  if ($[0] !== child) {
    t1 = () => {
      if (!child) {
        return;
      }
      trackEvent({
        event: "view_item",
        items: mapAnalyticsCartItems([convertChildToCart(child)]),
        currency: "USD"
      });
    };
    $[0] = child;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== child.globalId) {
    t2 = [child.globalId];
    $[2] = child.globalId;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  useEffect(t1, t2);
  return null;
};

// https://ciorg.atlassian.net/wiki/spaces/DX/pages/29026287637/Errors
// @TODO: Add error tracking to lots of places.
export const trackError = (errorMessage?: string) => {
  if (!errorMessage) {
    return;
  }
  trackEvent({
    event: 'error_message',
    // The error message presented to the user
    message: errorMessage
  });
};
export const TrackError: FC<{
  errorMessage?: string;
}> = t0 => {
  const $ = _c(3);
  const {
    errorMessage
  } = t0;
  let t1;
  let t2;
  if ($[0] !== errorMessage) {
    t1 = () => {
      trackError(errorMessage);
    };
    t2 = [errorMessage];
    $[0] = errorMessage;
    $[1] = t1;
    $[2] = t2;
  } else {
    t1 = $[1];
    t2 = $[2];
  }
  useEffect(t1, t2);
  return null;
};
export const trackOutageNotice = (payload: {
  severity: string;
  title?: string;
  message?: string;
  trackingId?: string;
  ctaLabel?: string;
  ctaHref?: string;
}) => {
  trackEvent({
    event: 'outage_notice',
    outage_severity: payload.severity,
    outage_title: payload.title || null,
    outage_message: payload.message || null,
    outage_tracking_id: payload.trackingId || null,
    outage_cta_label: payload.ctaLabel || null,
    outage_cta_href: payload.ctaHref || null
  });
};

// Accessibility: detect browser zoom and/or increased text size.
// Returns a ratio (1 = default, 1.5 = 150% zoom/text, etc.) and a boolean flag.
//
// Detection strategy:
// 1. Browser/page zoom (Ctrl/Cmd +): detected via devicePixelRatio change from baseline.
//    devicePixelRatio increases reliably across browsers when the user zooms in.
// 2. Text-only zoom / root font-size override: detected by measuring 1rem against 16px.
// 3. Pinch-zoom (mobile / trackpad): detected via visualViewport.scale.
// 4. Pre-existing page zoom: detected via outerWidth / innerWidth ratio. Catches users
//    who arrive already zoomed (common on Windows at 125%/150%).
// We report the *maximum* of all signals so that any mechanism triggers large-text mode.
//
// Caveat: Signal 4 (outerWidth/innerWidth) can be skewed by docked DevTools or browser
// sidebars. It is a best-effort heuristic, not pixel-perfect.

// Captured once on module load — assumed to be the user's default zoom level.
const initialDpr = globalThis.window === undefined ? 1 : globalThis.window.devicePixelRatio ?? 1;
export const getTextZoomLevel = (): {
  ratio: number;
  isLargeText: boolean;
} => {
  if (globalThis.window === undefined || typeof document === 'undefined') {
    return {
      ratio: 1,
      isLargeText: false
    };
  }

  // --- Signal 1: Page zoom (Ctrl/Cmd +/-) ---
  // devicePixelRatio increases with browser zoom (e.g. 1→1.5 on a 1x display,
  // 2→3 on a Retina display). Dividing by the initial value gives the zoom factor.
  //
  // Clamped to a minimum of 1 because sub-1 values are not meaningful for
  // accessibility tracking. They can occur when a user drags the browser window
  // from a high-DPI display (e.g. Retina, DPR 2) to a standard display (DPR 1)
  // after page load — the ratio becomes 0.5, which is not "zoomed out" but rather
  // a monitor change. Reporting 1 ("no zoom detected") is the correct interpretation.
  let pageZoomRatio = 1;
  if (globalThis.window.devicePixelRatio && initialDpr) {
    pageZoomRatio = Math.max(1, Math.round(globalThis.window.devicePixelRatio / initialDpr * 100) / 100);
  }

  // --- Signal 2: Text-size / root font-size override ---
  // Measures the computed size of 1rem. If the user changed the browser's default
  // font size (e.g., Chrome Settings → Font size → Very Large), 1rem will exceed 16px.
  const testEl = document.createElement('div');
  testEl.style.width = '1rem';
  testEl.style.height = '0';
  testEl.style.position = 'absolute';
  testEl.style.visibility = 'hidden';
  document.body.append(testEl);
  const remPx = testEl.getBoundingClientRect().width;
  testEl.remove();
  const baseline = 16;
  const textZoomRatio = Math.round(remPx / baseline * 100) / 100;

  // --- Signal 3: Pinch-zoom (mobile / trackpad) ---
  // visualViewport.scale reflects pinch-to-zoom level. On iOS Safari,
  // devicePixelRatio is fixed at the device's native DPR regardless of zoom,
  // so this is the only reliable signal for mobile pinch-zoom.
  // Also responds to trackpad pinch gestures on desktop — arguably correct since
  // the user is intentionally enlarging content.
  let pinchZoomRatio = 1;
  if (globalThis.window.visualViewport?.scale) {
    pinchZoomRatio = Math.round(globalThis.window.visualViewport.scale * 100) / 100;
  }

  // --- Signal 4: outerWidth / innerWidth (pre-existing page zoom) ---
  // Catches users who arrive with browser zoom already applied (e.g. Windows users
  // who permanently run at 125% or 150%). Signal 1 misses these because initialDpr
  // captures the already-zoomed state.
  //
  // outerWidth is the browser window's full width including chrome; innerWidth is
  // the viewport width which shrinks as zoom increases. The ratio approximates the
  // zoom factor. Caveats:
  // - Not reliable when DevTools is docked to the side (inflates outerWidth).
  // - Some browsers report 0 for outerWidth in certain contexts (e.g. popups).
  // - Sidebar panels (bookmarks, extensions) can skew the ratio slightly.
  // We only use this signal when both values are positive and the ratio is >= 1.
  let layoutZoomRatio = 1;
  if (globalThis.window.outerWidth > 0 && globalThis.window.innerWidth > 0) {
    const raw = Math.round(globalThis.window.outerWidth / globalThis.window.innerWidth * 100) / 100;
    layoutZoomRatio = Math.max(1, raw);
  }

  // Use the highest of all signals — any mechanism indicates enlarged content.
  const ratio = Math.max(pageZoomRatio, textZoomRatio, pinchZoomRatio, layoutZoomRatio);

  // Consider anything above 120% as "large text" mode
  const isLargeText = ratio >= 1.2;
  return {
    ratio,
    isLargeText
  };
};

// https://ciorg.atlassian.net/wiki/spaces/DX/pages/29089169443/User+Identification
export const trackUser = (authUser?: AuthClientResponse | null, cart?: Cart | null) => {
  // TODO: Probably should consolidate with sendInitialPushEvent from shared-utils
  const loggedIn = authUser && !!authUser.authUserGlobalId;
  const {
    ratio: textZoomRatio,
    isLargeText
  } = getTextZoomLevel();
  trackEvent({
    event: 'initial_push',
    is_logged_in: loggedIn,
    logged_in_state: loggedIn ? UserLoginState.LoggedIn : UserLoginState.Guest,
    // TODO: determine source code if no cart?
    source_code_id: cart?.sourceCode,
    supporter_global_id: authUser?.authUserGlobalId,
    user_id: authUser?.authUserPrimaryContactId,
    // Accessibility: text zoom detection
    text_zoom_ratio: textZoomRatio,
    is_large_text: isLargeText
  });
};
export const TrackUser: FC = () => {
  const $ = _c(9);
  const pathname = usePathname();
  const {
    isReady: isAuthReady,
    authUser
  } = useAuth("isReady", "isLoggedIn", "authUser");
  const {
    isReady: isCartReady,
    cart
  } = useCart("isReady", "cart");
  let t0;
  if ($[0] !== authUser || $[1] !== cart || $[2] !== isAuthReady || $[3] !== isCartReady) {
    t0 = () => {
      if (!isAuthReady || !isCartReady) {
        return;
      }
      trackUser(authUser, cart);
    };
    $[0] = authUser;
    $[1] = cart;
    $[2] = isAuthReady;
    $[3] = isCartReady;
    $[4] = t0;
  } else {
    t0 = $[4];
  }
  let t1;
  if ($[5] !== isAuthReady || $[6] !== isCartReady || $[7] !== pathname) {
    t1 = [pathname, isAuthReady, isCartReady];
    $[5] = isAuthReady;
    $[6] = isCartReady;
    $[7] = pathname;
    $[8] = t1;
  } else {
    t1 = $[8];
  }
  useEffect(t0, t1);
  return null;
};
export const TrackOptimizelyResults: FC = () => {
  const pathname = usePathname();
  useEffect(() => {
    const trackResults = async () => {
      try {
        const params = new URLSearchParams(globalThis?.location?.search);
        params.set('pathname', pathname);
        params.set('hostname', globalThis?.location?.hostname);
        const response = await fetch(`/api/experiments?${params.toString()}`);
        if (!response.ok) {
          return;
        }
        const data = await response.json();
        trackEvent({
          event: 'optimizely_results',
          uid: data?.uid,
          results: data?.results,
          user_attributes: data?.userAttributes
        });
      } catch (e) {
        // Do nothing if the API call fails.
        console.error(e);
      }
    };
    trackResults();
  }, [pathname]);
  return null;
};